import { createApi } from "@reduxjs/toolkit/query/react";
import { customFetchBaseQuery } from "./customFetchBaseQuery";
import { get } from "lodash";

type addToWishlistBodyType = {
  propertyId: string;
};

type removeFromWishlistType = {
  propertyId: string;
};

export const apiUserSlice = createApi({
  reducerPath: "api-user",
  baseQuery: customFetchBaseQuery,
  tagTypes: ["Wishlist", "ApnStatus", "Onboarding", "Plan"],
  keepUnusedDataFor: 300,
  // User-scoped data (wishlist, appliances, services, claim status). The
  // cache keys don't include the user, so without this a query cached for
  // one account would be served to the next account that logs in. Refetch
  // on mount so switching users always pulls the current user's data.
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => {
    return {
      addToWishlist: builder.mutation({
        query: (body: addToWishlistBodyType) => ({
          url: "/api/user/wishlist",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Wishlist"],
      }),
      getWishlist: builder.query({
        query: () => "/api/user/wishlist",
        providesTags: ["Wishlist"],
      }),
      getWishlistWithDetails: builder.query({
        query: () => "/api/user/wishlist/details",
        providesTags: ["Wishlist"],
      }),
      removeFromWishlist: builder.mutation({
        query: (body: removeFromWishlistType) => ({
          url: `/api/user/wishlist`,
          method: "DELETE",
          body,
        }),
        invalidatesTags: ["Wishlist"],
      }),
      getUserPropertyList: builder.query({
        query: (query) => "/api/user/property-list",
        keepUnusedDataFor: 0,
      }),
      getUserPropertyListOnlyIds: builder.query({
        query: (query) => "/api/user/property",
        transformResponse: (res: any) => {
          return get(res, "data", []).map((item: any) => item._id);
        },
        keepUnusedDataFor: 0,
      }),
      postClaimedPropertyRequest: builder.mutation({
        query: (body: any) => ({
          url: `/api/user/upload-apn-document`,
          method: "POST",
          body,
        }),
        // A new claim flips the home to PENDING — refresh the claim status (so
        // the property page's pending pop-up appears) and the site-wide signal.
        invalidatesTags: ["ApnStatus", "Onboarding"],
      }),
      getApnClaimStatus: builder.query({
        query: (propertyId: string) => `/api/user/apn-status/${propertyId}`,
        keepUnusedDataFor: 0,
        providesTags: ["ApnStatus"],
      }),
      // Onboarding pop-up: mark the approved-state welcome tour as seen for a
      // home so it fires only once. body { propertyId }.
      markOnboardingSeen: builder.mutation({
        query: (body: { propertyId: string }) => ({
          url: `/api/user/onboarding-seen`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["ApnStatus", "Onboarding"],
      }),
      // Onboarding pop-up: site-wide signal — the caller's homes that need a
      // welcome tour or a "needs more info" prompt. Polled by OnboardingManager.
      getOnboardingHomeStates: builder.query({
        query: () => `/api/user/onboarding/home-states`,
        transformResponse: (res: any) => get(res, "data.homes", []),
        keepUnusedDataFor: 0,
        providesTags: ["Onboarding"],
      }),
      // --- Billing (November 2026, Task 5) ---------------------------------
      // The caller's account + current plan ({ user, plan }). Always fresh so
      // /premium/welcome and My Account reflect a checkout/grant without a
      // re-login.
      getMe: builder.query({
        query: () => `/api/me`,
        transformResponse: (res: any) => get(res, "data", {}),
        keepUnusedDataFor: 0,
        providesTags: ["Plan"],
      }),
      // Start a Stripe Checkout for Premium. Returns { url } to redirect to.
      postBillingCheckout: builder.mutation<any, void>({
        query: () => ({ url: `/api/billing/checkout`, method: "POST", body: {} }),
        transformResponse: (res: any) => get(res, "data", {}),
      }),
      // Open the Stripe customer portal. Returns { url }.
      postBillingPortal: builder.mutation<any, void>({
        query: () => ({ url: `/api/billing/portal`, method: "POST", body: {} }),
        transformResponse: (res: any) => get(res, "data", {}),
      }),
      getUserServicesFolders: builder.query({
        query: (propertyId) => `/api/user/services/${propertyId}`,
        keepUnusedDataFor: 0,
      }),
      getUserApplianceFolders: builder.query({
        query: (propertyId) => `api/user/appliance/${propertyId}`,
        keepUnusedDataFor: 0,
      }),
      postCreateApplianceFolder: builder.mutation({
        query: (body: any) => ({
          url: `/api/user/appliance`,
          method: "POST",
          body,
        }),
      }),
      postCreateServiceFolder: builder.mutation({
        query: (body: any) => ({
          url: `/api/user/services`,
          method: "POST",
          body,
        }),
      }),
      postCreateApplianceItems: builder.mutation({
        query: (body: any) => {
          const folderId = body.get("folderId");
          return {
            url: `/api/user/appliance/${folderId}/items`,
            method: "POST",
            body: body,
          };
        },
      }),
      postCreateServiceItems: builder.mutation({
        query: (body: any) => {
          const folderId = body.get("folderId");
          return {
            url: `/api/user/services/${folderId}/items`,
            method: "POST",
            body: body,
          };
        },
      }),
      getAppliancesPreSignedUrl: builder.query({
        query: (body: any) => {
          return {
            url: `/api/user/appliances/getPreSignedUrl`,
            method: "POST",
            body: body,
          };
        },
      }),
      getServicesPreSignedUrl: builder.query({
        query: (body: any) => {
          return {
            url: `/api/user/services-url/getPreSignedUrl?fileName=${body}`,
            method: "GET",
          };
        },
      }),
      updateApplianceItem: builder.mutation({
        query: ({ folderId, itemId, formData, ...body }: { folderId: string; itemId: string; formData?: FormData; [key: string]: any }) => ({
          url: `/api/user/appliance/${folderId}/items/${itemId}`,
          method: "PATCH",
          body: formData || body,
        }),
      }),
      updateServiceItem: builder.mutation({
        query: ({ folderId, itemId, formData, ...body }: { folderId: string; itemId: string; formData?: FormData; [key: string]: any }) => ({
          url: `/api/user/services/${folderId}/items/${itemId}`,
          method: "PATCH",
          body: formData || body,
        }),
      }),
      deleteApplianceBill: builder.mutation({
        query: ({ folderId, itemId }: { folderId: string; itemId: string }) => ({
          url: `/api/user/appliance/${folderId}/items/${itemId}/bill`,
          method: "DELETE",
        }),
      }),
      deleteServiceBill: builder.mutation({
        query: ({ folderId, itemId }: { folderId: string; itemId: string }) => ({
          url: `/api/user/services/${folderId}/items/${itemId}/bill`,
          method: "DELETE",
        }),
      }),
    };
  },
});

export const {
  useAddToWishlistMutation,
  useRemoveFromWishlistMutation,
  useGetWishlistQuery,
  useGetWishlistWithDetailsQuery,
  useGetUserPropertyListOnlyIdsQuery,
  useLazyGetUserPropertyListOnlyIdsQuery,
  useGetUserPropertyListQuery,
  useLazyGetUserPropertyListQuery,
  useLazyGetWishlistQuery,
  usePostClaimedPropertyRequestMutation,
  useGetApnClaimStatusQuery,
  useLazyGetApnClaimStatusQuery,
  useMarkOnboardingSeenMutation,
  useGetOnboardingHomeStatesQuery,
  useGetMeQuery,
  useLazyGetMeQuery,
  usePostBillingCheckoutMutation,
  usePostBillingPortalMutation,
  usePostCreateApplianceFolderMutation,
  useLazyGetUserApplianceFoldersQuery,
  usePostCreateApplianceItemsMutation,
  useGetUserServicesFoldersQuery,
  useLazyGetUserServicesFoldersQuery,
  usePostCreateServiceFolderMutation,
  usePostCreateServiceItemsMutation,
  useLazyGetAppliancesPreSignedUrlQuery,
  useLazyGetServicesPreSignedUrlQuery,
  useUpdateApplianceItemMutation,
  useUpdateServiceItemMutation,
  useDeleteApplianceBillMutation,
  useDeleteServiceBillMutation,
} = apiUserSlice;
